import type { SkillBranch, Vector2 } from '@sorrem/shared';
import type { SkillTreeEffect, SkillTreeEffectColor } from './skill-tree-effects-renderer';
import type { SkillTreeDialModel, SkillTreeModel, SkillTreeNodeModel } from './skill-tree-model';
import { SKILL_TREE_BURST_DURATION, SKILL_TREE_INTRO_SPEED_MOD, type SkillTreeBurst, type SkillTreeIntroSchedule } from './skill-tree-intro';
import { SKILL_TREE_SIGIL_POSITION, SKILL_TREE_SIGIL_SIZE } from './skill-tree-layout';
import { toDialSelectionId, toGroupLinkKey, toSigilLinkKey, toSkillLinkKey, type SkillTreeEffectTarget } from './skill-tree-ids';
import { buildSkillTreeLinks, resolveLinkCenterline, type LinkSpec } from './skill-tree-link-geometry';
import { polar } from './skill-tree-math';
import {
    LIGHT_GOLD,
    LIGHT_GOLD_HOT,
    LIGHT_IRON,
    LIGHT_VIOLET,
    LIGHT_VIOLET_HOT,
    WHITE,
    hexToEffectColor,
} from './skill-tree-palette';
import {
    DIAL_ARC_RADIUS,
    DIAL_PIECE_SCALE,
    DIAL_START_ANGLE,
    DIAL_SWEEP,
    ESSENCE_RING_ANGLES,
    MARTIAL_RING_RADIUS,
    MARTIAL_SEGMENT_SWEEP,
    PIECE_CENTER,
    resolveEssenceSpiralPoint,
    resolveMartialSegmentStartAngle,
} from './skill-tree-pieces';

const GOLD = hexToEffectColor(LIGHT_GOLD);
const GOLD_HOT = hexToEffectColor(LIGHT_GOLD_HOT);
const VIOLET = hexToEffectColor(LIGHT_VIOLET);
const VIOLET_HOT = hexToEffectColor(LIGHT_VIOLET_HOT);

const BRANCH_COLORS: Record<SkillBranch, { readonly base: SkillTreeEffectColor; readonly hot: SkillTreeEffectColor }> = {
    martial: { base: GOLD, hot: GOLD_HOT },
    essence: { base: VIOLET, hot: VIOLET_HOT },
    crafting: { base: hexToEffectColor(LIGHT_IRON), hot: hexToEffectColor(WHITE) },
};

const SIGIL_TARGET: SkillTreeEffectTarget = { kind: 'sigil' };

/** Inactive (equipment-gated) skills keep a faint version of their light. */
const INACTIVE_DIMMING = 0.4;
/** Path sampling step along arcs and the spiral, in degrees. */
const ARC_STEP = 4;

/** Points along a circular arc around `center`, `fromAngle` to `toAngle` degrees clockwise from +x. */
function arcPath(center: Vector2, radius: number, fromAngle: number, toAngle: number): Vector2[] {
    const steps = Math.max(1, Math.ceil(Math.abs(toAngle - fromAngle) / ARC_STEP));
    return Array.from({ length: steps + 1 }, (_, index) => polar(center, radius, fromAngle + ((toAngle - fromAngle) * index) / steps));
}

/** The Essence spiral from the core out to `toAngle`, placed at a node's tree position. */
function spiralPath(position: Vector2, toAngle: number): Vector2[] {
    const steps = Math.max(1, Math.ceil(toAngle / ARC_STEP));
    return Array.from({ length: steps + 1 }, (_, index) => {
        const point = resolveEssenceSpiralPoint((toAngle * index) / steps);
        return { x: position.x + point.x - PIECE_CENTER.x, y: position.y + point.y - PIECE_CENTER.y };
    });
}

function martialNodeEffects(node: SkillTreeNodeModel, dim: number): SkillTreeEffect[] {
    const { id, position } = node.placement;
    const target: SkillTreeEffectTarget = { kind: 'skill', id };
    const effects: SkillTreeEffect[] = [
        { kind: 'halo', id: `${id}:halo`, target, center: position, radius: 64, color: GOLD, alpha: (0.12 + 0.03 * node.step) * dim },
    ];
    if (node.isMastered) {
        const ring = arcPath(position, MARTIAL_RING_RADIUS, 0, 360).slice(0, -1);
        effects.push(
            { kind: 'blaze', id: `${id}:blaze`, target, path: ring, closed: true, center: position, color: GOLD, hotColor: GOLD_HOT, alpha: 0.45 * dim, size: 14, spacing: 7, embers: 8 },
            { kind: 'glint', id: `${id}:glint`, target, path: ring, closed: true, color: GOLD, alpha: 0.9 * dim, count: 3, size: 8, speed: 40 },
        );
    } else if (!node.isInactive) {
        // A glint runs along the filled segments, up to the partly filled one.
        const end = node.progress > 0
            ? resolveMartialSegmentStartAngle(node.step) + MARTIAL_SEGMENT_SWEEP * node.progress
            : resolveMartialSegmentStartAngle(node.step - 1) + MARTIAL_SEGMENT_SWEEP;
        const arc = arcPath(position, MARTIAL_RING_RADIUS, resolveMartialSegmentStartAngle(0), end);
        effects.push({ kind: 'glint', id: `${id}:glint`, target, path: arc, closed: false, color: GOLD, alpha: 0.9, count: 1, size: 7, speed: 30 });
    }
    return effects;
}

function essenceNodeEffects(node: SkillTreeNodeModel, dim: number): SkillTreeEffect[] {
    const { id, position } = node.placement;
    const target: SkillTreeEffectTarget = { kind: 'skill', id };
    if (node.isMastered) {
        const spiral = spiralPath(position, ESSENCE_RING_ANGLES[ESSENCE_RING_ANGLES.length - 1]!);
        return [
            { kind: 'halo', id: `${id}:halo`, target, center: position, radius: 74, color: VIOLET, alpha: 0.45 * dim },
            { kind: 'blaze', id: `${id}:blaze`, target, path: spiral, closed: false, center: position, color: VIOLET, hotColor: VIOLET_HOT, alpha: 0.4 * dim, size: 10, spacing: 4, embers: 10 },
            { kind: 'glint', id: `${id}:glint`, target, path: spiral, closed: false, color: VIOLET_HOT, alpha: 0.9 * dim, count: 3, size: 6, speed: 35 },
            { kind: 'orbit', id: `${id}:motes`, target, center: position, radius: 62, color: VIOLET_HOT, alpha: 0.7 * dim, count: 10, size: 3.5 },
        ];
    }
    const effects: SkillTreeEffect[] = [
        { kind: 'halo', id: `${id}:halo`, target, center: position, radius: 64, color: VIOLET, alpha: (0.1 + 0.05 * node.step) * dim },
    ];
    if (!node.isInactive) {
        // Motes flow out along the lit spiral, into the ring that is filling.
        const reached = node.progress > 0 ? node.step : node.step - 1;
        const toAngle = ESSENCE_RING_ANGLES[Math.min(reached, ESSENCE_RING_ANGLES.length - 1)]!;
        effects.push({ kind: 'glint', id: `${id}:glint`, target, path: spiralPath(position, toAngle), closed: false, color: VIOLET_HOT, alpha: 0.9, count: Math.min(4, reached + 1), size: 5, speed: 22 });
    }
    return effects;
}

function nodeEffects(node: SkillTreeNodeModel): SkillTreeEffect[] {
    if (node.isUnawakened || (node.step === 0 && node.progress === 0)) return [];
    const dim = node.isInactive ? INACTIVE_DIMMING : 1;
    switch (node.placement.branch) {
        case 'martial':
            return martialNodeEffects(node, dim);
        case 'essence':
            return essenceNodeEffects(node, dim);
        case 'crafting':
            return [];
    }
}

function dialEffects(dial: SkillTreeDialModel): SkillTreeEffect[] {
    if (dial.isUnawakened || dial.percent <= 0) return [];
    const { branch, position } = dial.placement;
    const id = toDialSelectionId(branch);
    const target: SkillTreeEffectTarget = { kind: 'dial', branch };
    const colors = BRANCH_COLORS[branch];
    const radius = DIAL_ARC_RADIUS * DIAL_PIECE_SCALE;
    const fill = Math.min(1, dial.percent / 100);
    const effects: SkillTreeEffect[] = [
        { kind: 'halo', id: `${id}:halo`, target, center: position, radius: 85, color: colors.base, alpha: 0.08 + 0.2 * fill },
    ];
    if (fill >= 1) {
        const ring = arcPath(position, radius, 0, 360).slice(0, -1);
        effects.push({ kind: 'blaze', id: `${id}:blaze`, target, path: ring, closed: true, center: position, color: colors.base, hotColor: colors.hot, alpha: 0.45, size: 16, spacing: 8, embers: 10 });
    } else {
        const arc = arcPath(position, radius, DIAL_START_ANGLE, DIAL_START_ANGLE + DIAL_SWEEP * fill);
        effects.push({ kind: 'glint', id: `${id}:glint`, target, path: arc, closed: false, color: colors.base, alpha: 0.9, count: 1, size: 8, speed: 45 });
    }
    return effects;
}

/** The Veil's drifting motes around the sigil, more with each Veil milestone. */
function sigilEffects(model: SkillTreeModel): SkillTreeEffect[] {
    const { veilMilestones } = model.sigil;
    if (veilMilestones <= 0) return [];
    return [
        { kind: 'halo', id: 'sigil:halo', target: SIGIL_TARGET, center: SKILL_TREE_SIGIL_POSITION, radius: SKILL_TREE_SIGIL_SIZE * 0.6, color: VIOLET, alpha: 0.05 * veilMilestones },
        { kind: 'orbit', id: 'sigil:motes', target: SIGIL_TARGET, center: SKILL_TREE_SIGIL_POSITION, radius: SKILL_TREE_SIGIL_SIZE * 0.52, color: VIOLET_HOT, alpha: 0.6, count: 3 * veilMilestones, size: 4 },
    ];
}

/**
 * Link pulses: light flowing from each recently trained skill back along its
 * links to its dial and on to the sigil, stronger the more it trained over
 * the last few runs (docs/gameplay/skill-tree-ui.md, Links).
 */
function pulseEffects(model: SkillTreeModel): SkillTreeEffect[] {
    const linksByKey = new Map<string, LinkSpec>(buildSkillTreeLinks(model).map((link) => [link.key, link]));
    const backward = (key: string): Vector2[] => {
        const link = linksByKey.get(key);
        return link ? [...resolveLinkCenterline(link)].reverse() : [];
    };
    return model.nodes.flatMap((node): SkillTreeEffect[] => {
        const strength = node.recentTraining;
        if (strength <= 0 || node.isUnawakened) return [];
        const { id, branch, group } = node.placement;
        const path = [...backward(toSkillLinkKey(id)), ...backward(toGroupLinkKey(group)), ...backward(toSigilLinkKey(branch))];
        if (path.length < 2) return [];
        return [{
            kind: 'glint',
            id: `${id}:pulse`,
            target: { kind: 'skill', id },
            path,
            closed: false,
            color: BRANCH_COLORS[branch].hot,
            alpha: 0.35 + 0.55 * strength,
            count: 1 + Math.round(2 * strength),
            size: 5 + 3 * strength,
            speed: 90,
        }];
    });
}

export interface SkillTreeEffectsOptions {
    /** Time every effect to the Skills tab intro: each fades in as its part of the tree fills, mastered blazes last. */
    readonly schedule?: SkillTreeIntroSchedule;
    /** Seconds of the intro already played (it turned around partway), taken off every delay. */
    readonly offset?: number;
    /** Celebrations for what changed since the player last looked. */
    readonly bursts?: readonly SkillTreeBurst[];
    /** Real seconds after the effects are set before the bursts fire. */
    readonly burstDelay?: number;
}

/**
 * When an effect fades in during the intro, from the schedule: as its dial's
 * values or its node's entrance get under way, mastered blazes and motes
 * once those finish, and the sigil's motes as its enhancements settle.
 */
function introDelay(effect: SkillTreeEffect, schedule: SkillTreeIntroSchedule): number {
    const ignitesLast = effect.kind === 'blaze' || effect.kind === 'orbit';
    const { target } = effect;
    switch (target.kind) {
        case 'sigil':
            return schedule.sigilSettle.start;
        case 'dial': {
            const values = schedule.dials.get(target.branch)?.values;
            if (!values) return schedule.timeline;
            return ignitesLast ? values.start + values.duration : values.start + 0.2;
        }
        case 'skill': {
            const entrance = schedule.nodes.get(target.id)?.entrance;
            if (!entrance) return schedule.timeline;
            return ignitesLast ? entrance.start + entrance.duration : entrance.start + entrance.duration * 0.4;
        }
    }
}

function burstEffect(model: SkillTreeModel, burst: SkillTreeBurst, delay: number): SkillTreeEffect | null {
    const dial = model.dials.find((candidate) => toDialSelectionId(candidate.placement.branch) === burst.targetId);
    const node = model.nodes.find((candidate) => candidate.placement.id === burst.targetId);
    const placement = dial?.placement ?? node?.placement;
    if (!placement) return null;
    const target: SkillTreeEffectTarget = dial ? { kind: 'dial', branch: dial.placement.branch } : { kind: 'skill', id: burst.targetId };
    const colors = BRANCH_COLORS[placement.branch];
    const big = burst.kind !== 'level-up';
    return {
        kind: 'burst',
        id: `${burst.targetId}:burst`,
        target,
        center: placement.position,
        radius: big ? 110 : 75,
        color: colors.base,
        hotColor: colors.hot,
        alpha: big ? 1 : 0.8,
        sparks: burst.kind === 'mastery' ? 28 : big ? 20 : 14,
        duration: SKILL_TREE_BURST_DURATION,
        delay,
    };
}

/**
 * Chooses the animated light the effects layer draws for a character's
 * tree: halos that brighten with level, glints along lit rings, spirals,
 * and dial arcs, blazes on mastered skills and full dials, and Veil motes
 * around the sigil. Geometry comes from the same numbers as the SVG pieces.
 * With a `schedule`, each effect waits for its part of the tree to fill; bursts
 * celebrate what changed since the last look.
 */
export function buildSkillTreeEffects(model: SkillTreeModel, options: SkillTreeEffectsOptions = {}): SkillTreeEffect[] {
    const effects = [
        ...sigilEffects(model),
        ...model.dials.flatMap(dialEffects),
        ...model.nodes.flatMap(nodeEffects),
        ...pulseEffects(model),
    ];
    const { schedule } = options;
    const offset = options.offset ?? 0;
    // Delays are real seconds; the schedule's times play at the intro's speed.
    const timed = schedule
        ? effects.map((effect) => ({ ...effect, delay: (introDelay(effect, schedule) - offset) / SKILL_TREE_INTRO_SPEED_MOD }))
        : effects;
    const bursts = (options.bursts ?? [])
        .map((burst) => burstEffect(model, burst, options.burstDelay ?? 0))
        .filter((effect): effect is SkillTreeEffect => effect !== null);
    return [...timed, ...bursts];
}
